import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, X } from "lucide-react";
import { api, setSeo } from "@/lib/api";
import ProductGrid from "@/components/ProductGrid";
import { useSite } from "@/context/SiteContext";

const CAT_NAMES = { abayas: "Abayas", hijabs: "Hijabs", jilbabs: "Jilbabs", partywear: "Partywear Hijabs", malaysian: "Malaysian Hijabs", accessories: "Accessories", "hajj-umrah": "Hajj & Umrah" };
const ALL_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "One Size"];
const ALL_COLORS = ["Black", "Ivory", "Beige", "Champagne", "Nude", "Grey", "Sage", "Camel", "White", "Blush"];
const SEO = {
  abayas: ["Abayas Online India | Adam's Islamic Boutique", "Shop premium abayas online in India. Elegant black, beige & designer abayas at Adam's — abaya shop Chennai."],
  hijabs: ["Hijabs Online | Hijab Shop Chennai | Adam's", "Buy premium hijabs online — chiffon, satin, jersey & Malaysian hijabs. Hijab shop Chennai & across India."],
  jilbabs: ["Jilbab India | Buy Jilbabs Online | Adam's Boutique", "Shop elegant two-piece & overhead jilbabs online in India at Adam's The Islamic Boutique."],
};

export default function Shop() {
  const { category } = useParams();
  const [params] = useSearchParams();
  const search = params.get("search") || "";
  const { categories } = useSite();

  const [sort, setSort] = useState("newest");
  const [priceMax, setPriceMax] = useState(7000);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [mobileFilters, setMobileFilters] = useState(false);

  useEffect(() => {
    const s = SEO[category] || ["Shop Modest Fashion Online India | Adam's The Islamic Boutique", "Shop abayas, hijabs, jilbabs & accessories online in India. Premium Islamic modest fashion at Adam's."];
    setSeo(s[0], s[1]);
  }, [category]);

  useEffect(() => { setSizes([]); setColors([]); setPriceMax(7000); }, [category]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (search) p.set("search", search);
    if (sort) p.set("sort", sort);
    if (sizes.length) p.set("sizes", sizes.join(","));
    if (colors.length) p.set("colors", colors.join(","));
    p.set("max_price", String(priceMax));
    return p.toString();
  }, [category, search, sort, sizes, colors, priceMax]);

  const { data: products = [], isLoading } = useQuery({ queryKey: ["shop", qs], queryFn: async () => (await api.get(`/products?${qs}`)).data });

  const title = search ? `Results for "${search}"` : (categories.find((c) => c.key === category)?.name || CAT_NAMES[category] || "All Products");
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const FilterPanel = () => (
    <div className="space-y-8">
      <div>
        <h4 className="font-serif text-lg text-espresso mb-4">Categories</h4>
        <div className="flex flex-col gap-2">
          <a href="/shop" className={`text-sm ${!category ? "text-champagne font-medium" : "text-espresso/60"}`}>All Products</a>
          {categories.map((c) => (
            <a key={c.key} href={`/category/${c.key}`} className={`text-sm ${category === c.key ? "text-champagne font-medium" : "text-espresso/60 hover:text-espresso"}`}>{c.name}</a>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-serif text-lg text-espresso mb-4">Max Price: ₹{priceMax}</h4>
        <input type="range" min="300" max="7000" step="100" value={priceMax} onChange={(e) => setPriceMax(+e.target.value)} className="w-full accent-champagne" data-testid="price-filter" />
      </div>

      <div>
        <h4 className="font-serif text-lg text-espresso mb-4">Size</h4>
        <div className="flex flex-wrap gap-2">
          {ALL_SIZES.map((s) => (
            <button key={s} onClick={() => toggle(sizes, setSizes, s)} className={`px-3 py-1.5 rounded-full text-xs border ${sizes.includes(s) ? "border-champagne bg-champagne/10 text-espresso" : "border-espresso/20 text-espresso/60"}`} data-testid={`size-filter-${s}`}>{s}</button>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-serif text-lg text-espresso mb-4">Color</h4>
        <div className="flex flex-wrap gap-2">
          {ALL_COLORS.map((c) => (
            <button key={c} onClick={() => toggle(colors, setColors, c)} className={`px-3 py-1.5 rounded-full text-xs border ${colors.includes(c) ? "border-champagne bg-champagne/10 text-espresso" : "border-espresso/20 text-espresso/60"}`} data-testid={`color-filter-${c}`}>{c}</button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 lg:py-12">
      <div className="mb-8">
        <p className="overline mb-2">{search ? "Search" : "Collection"}</p>
        <h1 className="font-serif text-4xl lg:text-5xl text-espresso">{title}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        <aside className="hidden lg:block sticky top-32 self-start">
          <FilterPanel />
        </aside>

        <div>
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-champagne/20">
            <button onClick={() => setMobileFilters(true)} className="lg:hidden flex items-center gap-2 text-sm text-espresso" data-testid="mobile-filters-open">
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
            <p className="text-sm text-espresso/50 hidden lg:block">{products.length} products</p>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-espresso/20 rounded-full px-4 py-2 text-sm bg-ivory outline-none focus:border-champagne" data-testid="sort-select">
              <option value="newest">Sort by: Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
          <ProductGrid products={products} loading={isLoading} cols={3} paginate pageSize={12} />
        </div>
      </div>

      {/* Mobile filters drawer */}
      {mobileFilters && (
        <div className="fixed inset-0 z-[65] lg:hidden">
          <div className="absolute inset-0 bg-espresso/40" onClick={() => setMobileFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-ivory p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl text-espresso">Filters</h3>
              <button onClick={() => setMobileFilters(false)} data-testid="mobile-filters-close"><X className="w-6 h-6" /></button>
            </div>
            <FilterPanel />
            <button onClick={() => setMobileFilters(false)} className="w-full bg-espresso text-ivory py-3 rounded-full mt-8 text-sm">Show {products.length} Results</button>
          </div>
        </div>
      )}
    </div>
  );
}
